/**
 * Slow-motion trainer — a single-player practice aid that drops the simulation
 * to half speed for a few real-time seconds so a tricky strafe or transfer can
 * be felt and corrected, then recharges before it can be used again.
 *
 * Inspired by SurfUnity's slow-motion learning mode (MIT). The state machine is
 * driven by REAL (unscaled) seconds so the slow-mo window lasts a fixed wall-
 * clock duration regardless of the time scale it is applying. The Engine reads
 * `scale` to slow the fixed-tick accumulator.
 */
export const SLOWMO_SCALE = 0.5;
export const SLOWMO_DURATION = 4;
export const SLOWMO_COOLDOWN = 3;

export type SlowMotionPhase = 'ready' | 'active' | 'cooldown';

export class SlowMotion {
  private phaseState: SlowMotionPhase = 'ready';
  /** Seconds left in the current phase (active or cooldown). */
  private timer = 0;

  get phase(): SlowMotionPhase {
    return this.phaseState;
  }

  /** Time-scale to apply to the simulation: 0.5 while active, 1 otherwise. */
  get scale(): number {
    return this.phaseState === 'active' ? SLOWMO_SCALE : 1;
  }

  /** Toggle: start slow-mo when ready, or end it early (into cooldown) when active. */
  toggle(): void {
    if (this.phaseState === 'ready') {
      this.phaseState = 'active';
      this.timer = SLOWMO_DURATION;
    } else if (this.phaseState === 'active') {
      this.phaseState = 'cooldown';
      this.timer = SLOWMO_COOLDOWN;
    }
    // cooldown: ignore — must recharge first.
  }

  /** Advance by REAL (unscaled) seconds. */
  tick(dt: number): void {
    if (this.phaseState === 'ready') {
      return;
    }
    this.timer -= dt;
    if (this.timer > 0) {
      return;
    }
    if (this.phaseState === 'active') {
      this.phaseState = 'cooldown';
      this.timer = SLOWMO_COOLDOWN;
    } else {
      this.phaseState = 'ready';
      this.timer = 0;
    }
  }

  reset(): void {
    this.phaseState = 'ready';
    this.timer = 0;
  }

  /** HUD status line, or '' when ready/idle. */
  hudLine(): string {
    if (this.phaseState === 'active') {
      return `◐ SLOW-MO ${this.timer.toFixed(1)}s`;
    }
    if (this.phaseState === 'cooldown') {
      return `slow-mo recharge ${this.timer.toFixed(1)}s`;
    }
    return '';
  }
}
